import { useLayers } from "../../context/LayersContext";
import {
  mockLayers,
  defaultDimensionValue,
  wmsDimensionParam,
} from "../../global/constants/layers";
import { isProtectedWorkspace } from "../../services/geoServerAuth";
import { AuthenticatedWMSTileLayer } from "../AuthenticatedWMSTileLayer";
import { Z } from "../../global/constants/zIndex";
import { getLayerStyle } from "../../global/layerStyles";
import { Fragment, useEffect, useMemo, useState } from "react";
import { GeoJSONData } from "../GeoJSONData";
import { ScaleDependentLayer } from "../ScaleDependentLayer";
import { TemporalWMSLayer } from "../TemporalWMSLayer";
import { TiledWMSLayer } from "../TiledWMSLayer";
import { FilteredRasterLayer } from "../FilteredRasterLayer";
import { RangeStyledWMSLayer } from "../RangeStyledWMSLayer";
import {
  RASTER_FILTER_META,
  rangeToTileParams,
} from "../../global/constants/rasterValueFilters";
import { ElevationWMSLayer } from "../ElevationWMSLayer";
import { WindyLayer } from "../WindyLayer";
import { buildCqlFilter } from "../../utils/layerCqlFilter";
import { buildContourSldBody, filterContourLevels } from "../../global/rangeSld";
import type { PathOptions } from "leaflet";
import { GeoJSON } from "react-leaflet";
import { geoServer } from "../../services/geoServer";
import { useErrorToast } from "../../hooks/useErrorToast";
import { CONJ_DGC_LAYER_NAME } from "../../global/constants/dgcConjuntos";
import { useDgcChart } from "../../context/DgcChartContext";
import { VOCACAO_LAYER_NAME } from "../../global/constants/categoricalLegends";
import { hasVocacao } from "../../utils/vocacoes";
import { VocacaoDetailModal } from "../VocacaoDetailModal";

// Default z-index for layers without explicit zIndex
const DEFAULT_LAYER_Z_INDEX = Z.MAP_LAYER_TOP;

type Position = [number, number];
type LinearRing = Position[];
type PolygonCoordinates = LinearRing[];

const VOCACAO_HIGHLIGHT_STYLE: PathOptions = {
  color: "#facc15",
  weight: 4,
  opacity: 1,
  fillOpacity: 0,
};

const BRASIL_STYLE_MASK: PathOptions = {
  color: "transparent",
  fillColor: "#1a1a1a",
  fillOpacity: 0.2,
  interactive: false,
};

const BRASIL_GEOJSON_URL = `${(process.env.PUBLIC_URL || "").replace(
  /\/$/,
  ""
)}/data/br.json`;

function isPosition(value: unknown): value is Position {
  return (
    Array.isArray(value) &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  );
}

function isLinearRing(value: unknown): value is LinearRing {
  return Array.isArray(value) && value.every(isPosition);
}

function getOuterRings(geometry: any): LinearRing[] {
  if (geometry?.type === "Polygon" && isLinearRing(geometry.coordinates?.[0])) {
    return [geometry.coordinates[0]];
  }

  if (
    geometry?.type === "MultiPolygon" &&
    Array.isArray(geometry.coordinates)
  ) {
    return geometry.coordinates
      .map((polygon: unknown) => (Array.isArray(polygon) ? polygon[0] : null))
      .filter(isLinearRing);
  }

  return [];
}

function getRingCentroid(ring: LinearRing): Position | null {
  const points =
    ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring;

  if (points.length === 0) return null;

  const [lngSum, latSum] = points.reduce(
    ([lng, lat], [pointLng, pointLat]) => [lng + pointLng, lat + pointLat],
    [0, 0]
  );

  return [lngSum / points.length, latSum / points.length];
}

function isPointOnSegment(point: Position, start: Position, end: Position) {
  const [lng, lat] = point;
  const [startLng, startLat] = start;
  const [endLng, endLat] = end;
  const tolerance = 1e-10;

  if (
    lng < Math.min(startLng, endLng) - tolerance ||
    lng > Math.max(startLng, endLng) + tolerance ||
    lat < Math.min(startLat, endLat) - tolerance ||
    lat > Math.max(startLat, endLat) + tolerance
  ) {
    return false;
  }

  const cross =
    (lat - startLat) * (endLng - startLng) -
    (lng - startLng) * (endLat - startLat);

  if (Math.abs(cross) > tolerance) return false;

  const dot =
    (lng - startLng) * (endLng - startLng) +
    (lat - startLat) * (endLat - startLat);
  const squaredLength =
    (endLng - startLng) * (endLng - startLng) +
    (endLat - startLat) * (endLat - startLat);

  return dot >= 0 && dot <= squaredLength;
}

function isPointInRing(point: Position, ring: LinearRing) {
  const [lng, lat] = point;
  let isInside = false;

  for (
    let index = 0, previous = ring.length - 1;
    index < ring.length;
    previous = index++
  ) {
    const currentPoint = ring[index];
    const previousPoint = ring[previous];

    if (isPointOnSegment(point, previousPoint, currentPoint)) return true;

    const [currentLng, currentLat] = currentPoint;
    const [previousLng, previousLat] = previousPoint;
    const intersects =
      currentLat > lat !== previousLat > lat &&
      lng <
        ((previousLng - currentLng) * (lat - currentLat)) /
          (previousLat - currentLat) +
          currentLng;

    if (intersects) isInside = !isInside;
  }

  return isInside;
}

function findContainingPolygonIndex(
  polygons: PolygonCoordinates[],
  ring: LinearRing
) {
  const centroid = getRingCentroid(ring);
  const candidatePoints = centroid ? [centroid, ...ring] : ring;

  return polygons.findIndex((polygon) => {
    const outerRing = polygon[0];

    return (
      isLinearRing(outerRing) &&
      candidatePoints.some((point) => isPointInRing(point, outerRing))
    );
  });
}

function ferroviasAfterEachFeature(feature: any, layer: any) {
  const tipSituac: string = (feature?.properties?.tip_situac ?? "")
    .trim()
    .toLowerCase();

  if (!tipSituac.startsWith("desativ")) return;

  layer.on("add", () => {
    const path: SVGElement | undefined = layer._path;
    if (path) {
      // start/end markers cover simple two-vertex LineStrings, which have no mid vertices
      path.setAttribute("marker-start", "url(#ferrovia-cross)");
      path.setAttribute("marker-mid", "url(#ferrovia-cross)");
      path.setAttribute("marker-end", "url(#ferrovia-cross)");
    }
  });
}

export function RenderGeoServerLayers() {
  const errorToast = useErrorToast();
  const { openConjunto } = useDgcChart();

  const {
    layers,
    layerTimeValues,
    layerElevationValues,
    layerCqlFilters,
    layerValueRanges,
  } = useLayers();

  const [brGeoJson, setBrGeoJson] = useState<any | null>(null);
  const [peGeoJson, setPeGeoJson] = useState<any | null>(null);
  // Município clicado na camada Vocações Econômicas (abre o modal de detalhe).
  const [vocacaoFeature, setVocacaoFeature] = useState<any | null>(null);

  const brazilMaskWithHolePernambuco = useMemo(() => {
    if (!brGeoJson || !peGeoJson) return null;

    const mask = structuredClone(brGeoJson) as any;
    const peOuterRings = getOuterRings(peGeoJson?.features?.[0]?.geometry);

    if (peOuterRings.length === 0) return null;

    mask.features.forEach((feature: any) => {
      const geometry = feature?.geometry;

      if (!geometry) return;

      if (geometry.type === "Polygon") {
        peOuterRings.forEach((ring) => {
          const polygon = geometry.coordinates;
          const polygonIndex = findContainingPolygonIndex([polygon], ring);

          if (polygonIndex === 0) polygon.push(ring);
        });
      } else if (geometry.type === "MultiPolygon") {
        peOuterRings.forEach((ring) => {
          const polygonIndex = findContainingPolygonIndex(
            geometry.coordinates,
            ring
          );

          if (polygonIndex >= 0) geometry.coordinates[polygonIndex].push(ring);
        });
      }
    });

    return mask;
  }, [brGeoJson, peGeoJson]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchMaskGeoJson() {
      try {
        const [brFromPublic, peFromGeoServer] = await Promise.all([
          fetch(BRASIL_GEOJSON_URL, { signal }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Falha ao carregar br.json: ${response.status}`);
            }

            return response.json();
          }),
          geoServer.get("pepe-atlas/wms", {
            params: {
              service: "WMS",
              version: "1.1.0",
              request: "GetMap",
              layers: "pepe-atlas:estado_pe",
              bbox: "-41.3583362,-9.4828974,-32.3777679,-3.8046222",
              width: 768,
              height: 485,
              srs: "EPSG:4326",
              styles: "",
              format: "geojson",
            },
            signal,
          }),
        ]);

        if (!signal.aborted) {
          setBrGeoJson(brFromPublic);
          setPeGeoJson(peFromGeoServer.data);
        }
      } catch (error) {
        if (
          signal.aborted ||
          (error as Error).name === "AbortError" ||
          (error as Error).name === "CanceledError"
        ) {
          return;
        }

        errorToast((error as Error).message);
      }
    }

    fetchMaskGeoJson();

    return () => {
      controller.abort();
    };
  }, [errorToast]);

  // Filter for GeoServer layers (those with workspace)
  // Include layers that are either selected by user OR marked as permanent
  const geoserverLayers = mockLayers
    .filter(
      (layer) =>
        layer.workspace &&
        (layer.permanent ||
          layers.includes(layer.title) ||
          // Also render when a partial voltage filter is active (layer not in `layers` array)
          (layer.voltageOptions &&
            (layerCqlFilters[layer.layerName]?.length ?? 0) > 0))
    )
    // Sort by zIndex to ensure proper rendering order (lower zIndex renders first/below)
    .sort((a, b) => {
      const zIndexA = a.zIndex ?? DEFAULT_LAYER_Z_INDEX;
      const zIndexB = b.zIndex ?? DEFAULT_LAYER_Z_INDEX;
      return zIndexA - zIndexB;
    });

  const vocacaoLayer = geoserverLayers.find(
    (layer) => layer.layerName === VOCACAO_LAYER_NAME
  );
  const isVocacaoActive = !!vocacaoLayer;

  // Desativar a camada fecha o detalhe do município.
  useEffect(() => {
    if (!isVocacaoActive) setVocacaoFeature(null);
  }, [isVocacaoActive]);

  return (
    <>
      {/* Render the mask only ONCE */}
      {brazilMaskWithHolePernambuco && (
        <GeoJSON
          data={brazilMaskWithHolePernambuco as any}
          style={BRASIL_STYLE_MASK}
        />
      )}
      {geoserverLayers.map((layer) => {
        const style = getLayerStyle(layer.layerName);
        const wmsTypename = layer.wmsLayerName ?? layer.layerName;
        const detailedWmsLayerName = layer.wmsLayerName
          ? layer.detailedWmsLayerName
          : layer.detailedLayerName;
        const customIcon = layer.style?.customIcon;
        const customIconSize = layer.style?.customIconSize;
        const selectedVoltages = layerCqlFilters[layer.layerName] ?? [];
        const voltageCql = buildCqlFilter(
          layer.voltageOptions,
          selectedVoltages
        );
        const cqlFilter =
          [layer.cqlFilter, voltageCql].filter(Boolean).join(" AND ") ||
          undefined;

        // Route to WindyLayer for particle visualization
        if (layer.type === "particle") {
          const timeValue =
            layerTimeValues[layer.layerName] || layer.temporal?.range.start;
          return (
            <WindyLayer
              key={layer.layerName}
              typename={layer.layerName}
              workspace={layer.workspace!}
              zIndex={layer.zIndex}
              timeValue={timeValue}
              velocityOptions={layer.velocityOptions}
            />
          );
        }

        // Vetor estilizado no servidor: a simbologia mora num SLD do GeoServer,
        // não em layerStyles. Usado pelas camadas do SGB cujo estilo oficial tem
        // centenas de classes litoestratigráficas.
        if (layer.type === "wms") {
          return (
            <TiledWMSLayer
              key={layer.layerName}
              typename={layer.layerName}
              workspace={layer.workspace!}
              zIndex={layer.zIndex}
              opacity={layer.opacity}
              detailedLayerName={layer.detailedLayerName}
              detailedLayerZoomThreshold={layer.detailedLayerZoomThreshold}
            />
          );
        }

        // Route to appropriate WMS layer based on temporal dimension
        if (layer.type === "raster") {
          // Faixa de valor escolhida pelo usuário. Vale para qualquer raster
          // com rampa contínua e vem antes do roteamento normal, inclusive das
          // camadas com dimensão de elevação/tempo — a dimensão selecionada
          // segue junto no pedido WMS.
          const valueRange = layerValueRanges[layer.layerName];
          if (valueRange) {
            const meta = RASTER_FILTER_META[layer.layerName] ?? {};
            const { minVal, maxVal } = rangeToTileParams(valueRange);
            if (
              minVal !== undefined &&
              maxVal !== undefined &&
              minVal > maxVal
            )
              return null;

            const rangeOverlay = layer.rasterRangeOverlay;
            const rangeOverlayLayer = (() => {
              if (!rangeOverlay) return null;

              const overlayWorkspace = rangeOverlay.workspace ?? layer.workspace!;
              const overlayLayerName = rangeOverlay.layerName ?? layer.layerName;
              const contourLevels = rangeOverlay.contourLevels
                ? filterContourLevels(rangeOverlay.contourLevels, minVal, maxVal)
                : null;

              if (contourLevels && contourLevels.length === 0) return null;

              return (
                <TiledWMSLayer
                  key={`${layer.layerName}-range-overlay`}
                  typename={overlayLayerName}
                  workspace={overlayWorkspace}
                  styles={
                    contourLevels ? undefined : rangeOverlay.styleName
                  }
                  sldBody={
                    contourLevels
                      ? buildContourSldBody({
                          workspace: overlayWorkspace,
                          layerName: overlayLayerName,
                          levels: contourLevels,
                        })
                      : undefined
                  }
                  zIndex={
                    (layer.zIndex ?? DEFAULT_LAYER_Z_INDEX) +
                    (rangeOverlay.zIndexOffset ?? 1)
                  }
                  opacity={rangeOverlay.opacity ?? layer.opacity}
                  detailedLayerName={rangeOverlay.detailedLayerName}
                  detailedLayerZoomThreshold={
                    rangeOverlay.detailedLayerZoomThreshold ??
                    layer.detailedLayerZoomThreshold
                  }
                />
              );
            })();
            // Os dois caminhos recortam a mesma faixa; mudam as cores. O
            // backend pinta os pixels em cor sólida (bom para ler a máscara),
            // o GeoServer preserva o gradiente original via SLD dinâmico.
            if (valueRange.solidColor && meta.tileKey) {
              return (
                <Fragment key={`${layer.layerName}-filtered`}>
                  <FilteredRasterLayer
                    layerKey={meta.tileKey}
                    minVal={minVal}
                    maxVal={maxVal}
                    opacity={layer.opacity}
                    zIndex={layer.zIndex}
                  />
                  {rangeOverlayLayer}
                </Fragment>
              );
            }
            return (
              <Fragment key={`${layer.layerName}-range-styled`}>
                <RangeStyledWMSLayer
                  key={`${layer.layerName}-range-styled`}
                  typename={layer.layerName}
                  workspace={layer.workspace!}
                  detailedLayerName={layer.detailedLayerName}
                  detailedLayerZoomThreshold={layer.detailedLayerZoomThreshold}
                  minVal={minVal}
                  maxVal={maxVal}
                  opacity={layer.opacity}
                  zIndex={layer.zIndex}
                  elevationValue={
                    layer.temporal?.enabled &&
                    layer.temporal.dimension !== "time"
                      ? layerElevationValues[layer.layerName] ??
                        defaultDimensionValue(layer)
                      : undefined
                  }
                  dimensionParam={
                    layer.temporal?.dimension
                      ? wmsDimensionParam(layer.temporal.dimension)
                      : undefined
                  }
                  timeValue={
                    layer.temporal?.dimension === "time"
                      ? layerTimeValues[layer.layerName] ??
                        layer.temporal?.range.start
                      : undefined
                  }
                />
                {rangeOverlayLayer}
              </Fragment>
            );
          }

          // Dimension-based layer (elevation, or an ImageMosaic custom dimension
          // such as direction/year). All of them share the crossfading overlay;
          // only the WMS parameter name changes.
          if (layer.temporal?.enabled && layer.temporal.dimension !== "time") {
            const dimensionValue =
              layerElevationValues[layer.layerName] ??
              defaultDimensionValue(layer);
            return (
              <ElevationWMSLayer
                key={layer.layerName}
                typename={wmsTypename}
                workspace={layer.workspace!}
                zIndex={layer.zIndex}
                elevationValue={dimensionValue}
                dimensionParam={wmsDimensionParam(layer.temporal.dimension)}
                opacity={layer.opacity}
              />
            );
          }

          // Static raster (no temporal): use tiled WMS so GeoWebCache can cache tiles.
          // Workspaces protegidos precisam do tile autenticado, porque o
          // WMSTileLayer padrão carrega tiles em `<img>`, que não leva header.
          if (!layer.temporal?.enabled) {
            const TileLayerComponent = isProtectedWorkspace(layer.workspace)
              ? AuthenticatedWMSTileLayer
              : TiledWMSLayer;
            return (
              <TileLayerComponent
                key={layer.layerName}
                typename={wmsTypename}
                workspace={layer.workspace!}
                zIndex={layer.zIndex}
                opacity={layer.opacity}
                detailedLayerName={detailedWmsLayerName}
                detailedLayerZoomThreshold={layer.detailedLayerZoomThreshold}
              />
            );
          }

          // Time-based temporal layer
          const timeValue =
            layerTimeValues[layer.layerName] || layer.temporal.range.start;
          return (
            <TemporalWMSLayer
              key={layer.layerName}
              typename={wmsTypename}
              workspace={layer.workspace!}
              zIndex={layer.zIndex}
              timeValue={timeValue}
              opacity={layer.opacity}
            />
          );
        }

        // Use ScaleDependentLayer if useWmsPreview is enabled
        if (layer.useWmsPreview) {
          // Extract fill color from the style function to override the WMS preview color —
          // fillColor is the semantically meaningful area color; color is just the thin
          // stroke outline (near-black), which made point-layer previews look black.
          let wmsColor: string | undefined;
          if (style && typeof style === "function") {
            try {
              const s = style({ properties: {} });
              wmsColor = s.fillColor ?? s.color;
            } catch {
              // ignore
            }
          }
          return (
            <ScaleDependentLayer
              key={`${layer.layerName}-${cqlFilter ?? ""}`}
              typename={layer.layerName}
              workspace={layer.workspace!}
              style={style}
              customIcon={customIcon}
              customIconSize={customIconSize}
              zIndex={layer.zIndex}
              useWmsPreview={layer.useWmsPreview}
              minZoomForFeatures={layer.minZoomForFeatures}
              cqlFilter={cqlFilter}
              wmsColor={wmsColor}
            />
          );
        }

        // Otherwise use regular GeoJSONData
        return (
          <GeoJSONData
            key={`${layer.layerName}-${cqlFilter ?? ""}`}
            typename={layer.layerName}
            workspace={layer.workspace!}
            style={style}
            customIcon={customIcon}
            customIconSize={customIconSize}
            zIndex={layer.zIndex}
            cqlFilter={cqlFilter}
            minZoom={layer.minZoomForFeatures}
            fetchOnce={!!layer.permanent}
            afterEachFeature={
              layer.layerName === "ferrovias"
                ? ferroviasAfterEachFeature
                : undefined
            }
            onFeatureClick={
              layer.layerName === CONJ_DGC_LAYER_NAME
                ? (feature) => openConjunto(feature?.properties ?? {})
                : layer.layerName === VOCACAO_LAYER_NAME
                ? setVocacaoFeature
                : undefined
            }
            isFeatureClickable={
              layer.layerName === VOCACAO_LAYER_NAME
                ? (feature) => hasVocacao(feature?.properties)
                : undefined
            }
          />
        );
      })}

      {vocacaoFeature && vocacaoLayer && (
        // Contorno do município aberto no modal, no mesmo pane da camada para
        // ficar por cima dos polígonos dela.
        <GeoJSON
          key={vocacaoFeature.id ?? vocacaoFeature.properties?.nm_mun}
          data={vocacaoFeature}
          style={VOCACAO_HIGHLIGHT_STYLE}
          interactive={false}
          pane={
            vocacaoLayer.zIndex !== undefined
              ? `pane-${VOCACAO_LAYER_NAME}`
              : undefined
          }
        />
      )}
      <VocacaoDetailModal
        feature={vocacaoFeature}
        onClose={() => setVocacaoFeature(null)}
      />
    </>
  );
}
